import { FC, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiSelectPhoneWithTextInput,
  MuiSelectWithTextInput,
  MuiToggleSwitch,
  OptionsDataType,
} from "../../../../../components";
import {
  MAX_LENGTH_100,
  MAX_LENGTH_15,
  MAX_LENGTH_50,
} from "../../../../../helpers/constants";
import { commonSiteCoreKeys } from "../../../../../helpers/constants/siteCoreKeys";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { ProgramSearchProgramFormKey } from "../../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { ParticipantsNameFields } from "../participantsNameFields/ParticipantsNameFields";

interface IndividualsSearchFieldsProps {
  pageId: string;
  resetForm: boolean;
  filterDropdownValue: OptionsDataType[];
}
export const IndividualsSearchFields: FC<IndividualsSearchFieldsProps> = ({
  pageId,
  resetForm,
  filterDropdownValue,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { getValues, control } = useFormContext();

  const {
    commonLabels,
    fetchDropdownValues,
    fetchValidationMessage,
    useFetchLabel,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.searchFormKey);

  const {
    p3ParticipantEmailPrimary,
    p3ParticipantEmailAlternate,
    p3ParticipantMobileNumberPrimary,
    p3ParticipantMobileNumberAlternate,
    nextParticipantEmailPrimary,
    nextParticipantEmailAlternate,
  } = programSearchSiteCoreKeys;

  const isP3PrimaryEmailToggleEnabled = useWatch({
    name: ProgramSearchProgramFormKey.isPrimaryParticipantEmail,
    control,
  });
  const isP3PrimaryMobileToggleEnabled = useWatch({
    name: ProgramSearchProgramFormKey.isPrimaryParticipantMobileNumber,
    control,
  });
  const isNEXTPrimaryEmailToggleEnabled = useWatch({
    name: ProgramSearchProgramFormKey.isPrimaryNEXTParticipantEmail,
    control,
  });

  const p3ParticipantEmailLabel = useMemo(
    () =>
      isP3PrimaryEmailToggleEnabled
        ? p3ParticipantEmailAlternate
        : p3ParticipantEmailPrimary,
    [isP3PrimaryEmailToggleEnabled],
  );

  const p3ParticipantMobileNumberLabel = useMemo(
    () =>
      isP3PrimaryMobileToggleEnabled
        ? p3ParticipantMobileNumberAlternate
        : p3ParticipantMobileNumberPrimary,
    [isP3PrimaryMobileToggleEnabled],
  );

  const nextParticipantEmailLabel = useMemo(
    () =>
      isNEXTPrimaryEmailToggleEnabled
        ? nextParticipantEmailAlternate
        : nextParticipantEmailPrimary,
    [isNEXTPrimaryEmailToggleEnabled],
  );

  const clientManagedStatus = fetchDropdownValues(
    programSearchSiteCoreKeys.searchFormKey,
    programSearchSiteCoreKeys.clientManagedStatusList,
  );

  const participantStatusDropdownValue = fetchDropdownValues(
    programSearchSiteCoreKeys.searchFormKey,
    programSearchSiteCoreKeys.participantStatusList,
  );

  return (
    <MuiGrid container className="form-container">
      <ParticipantsNameFields
        filterDropdownValue={filterDropdownValue}
        pageId={pageId}
      />
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.keyNumber}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(programSearchSiteCoreKeys.programKey)}
            placeholder={commonLabels.inputPlaceHolder}
            control={control}
            selectBoxName={ProgramSearchProgramFormKey.keyNumberFilter}
            id={`${pageId}-${ProgramSearchProgramFormKey.keyNumber}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.keyNumberFilter}`}
            maxLength={MAX_LENGTH_15}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.clientName}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(programSearchSiteCoreKeys.clientName)}
            placeholder={commonLabels.inputPlaceHolder}
            control={control}
            selectBoxName={ProgramSearchProgramFormKey.clientNameFilter}
            id={`${pageId}-${ProgramSearchProgramFormKey.clientName}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.clientNameFilter}`}
            maxLength={MAX_LENGTH_100}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <MuiBox className="toggle-parent-container">
          <FormController
            control={control}
            name={ProgramSearchProgramFormKey.participantEmail}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getLabel(p3ParticipantEmailLabel)}
              placeholder={commonLabels.emailPlaceHolder}
              control={control}
              selectBoxName={ProgramSearchProgramFormKey.participantEmailFilter}
              id={`${pageId}-${ProgramSearchProgramFormKey.participantEmail}`}
              selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.participantEmailFilter}`}
              maxLength={MAX_LENGTH_50}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
          <FormControllerCheckbox
            control={control}
            name={ProgramSearchProgramFormKey.isPrimaryParticipantEmail}
          >
            <MuiToggleSwitch className="toggle-container" />
          </FormControllerCheckbox>
        </MuiBox>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <MuiBox className="toggle-parent-container">
          <FormController
            control={control}
            name={ProgramSearchProgramFormKey.nextParticipantEmail}
          >
            <MuiSelectWithTextInput
              options={filterDropdownValue}
              label={getLabel(nextParticipantEmailLabel)}
              placeholder={commonLabels.emailPlaceHolder}
              control={control}
              selectBoxName={
                ProgramSearchProgramFormKey.nextParticipantEmailFilter
              }
              id={`${pageId}-${ProgramSearchProgramFormKey.nextParticipantEmail}`}
              selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.nextParticipantEmailFilter}`}
              maxLength={MAX_LENGTH_50}
              requiredARIALabel={commonARIALabel.required}
            />
          </FormController>
          <FormControllerCheckbox
            control={control}
            name={ProgramSearchProgramFormKey.isPrimaryNEXTParticipantEmail}
          >
            <MuiToggleSwitch className="toggle-container" />
          </FormControllerCheckbox>
        </MuiBox>
      </MuiGrid>

      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <MuiBox className="toggle-parent-container">
          <FormController
            control={control}
            name={ProgramSearchProgramFormKey.participantMobileNumber}
          >
            <MuiSelectPhoneWithTextInput
              ignoreError
              label={getLabel(p3ParticipantMobileNumberLabel)}
              defaultValue={getValues(
                ProgramSearchProgramFormKey.participantMobileNumber,
              )}
              defaultSelectedCountry={getValues(
                ProgramSearchProgramFormKey.mobileISOCode,
              )}
              placeholder={commonLabels.telephonePlaceHolder}
              phoneNumberFormId={
                ProgramSearchProgramFormKey.participantMobileNumber
              }
              countryCodeFormId={ProgramSearchProgramFormKey.mobileCountryCode}
              countryIsoCodeFormId={ProgramSearchProgramFormKey.mobileISOCode}
              reset={resetForm}
              withSelectOption
              searchPlaceholder={commonLabels.search}
              searchNotFound={commonLabels.noResult}
              selectLabel={commonLabels.selectPlaceHolder}
              invalidPhoneNumberMessage={fetchValidationMessage(
                commonSiteCoreKeys.invalidPhoneNumber,
              )}
            />
          </FormController>
          <FormControllerCheckbox
            control={control}
            name={ProgramSearchProgramFormKey.isPrimaryParticipantMobileNumber}
          >
            <MuiToggleSwitch className="toggle-container toggle-phone-container" />
          </FormControllerCheckbox>
        </MuiBox>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.programStatus}
        >
          <MuiSelect
            label={getLabel(programSearchSiteCoreKeys.participantStatus)}
            options={participantStatusDropdownValue}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${ProgramSearchProgramFormKey.programStatus}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.clientManagedStatus}
        >
          <MuiSelect
            label={getLabel(programSearchSiteCoreKeys.clientManagedStatus)}
            options={clientManagedStatus}
            value=""
            visibleDefaultSelectOption
            id={`${pageId}-${ProgramSearchProgramFormKey.clientManagedStatus}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
